import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ErrorDeLaApi, consulta, pedir } from '../../api/nucleo'
import { Aviso, Boton, Campo, Modal, Selector, clases, claseDeEntrada } from '../../componentes/ui'
import { textoDeFecha } from './calendario'
import type { Calendario, CampoDeReserva, Hueco } from './tipos'

/**
 * «Nueva reserva» desde el panel: la cita que llega por teléfono, por WhatsApp o en persona. Pasa por la
 * misma puerta que la web (`POST /api/agenda/reservas`), que comprueba el hueco con las filas bloqueadas;
 * desde aquí no se exige lo obligatorio de las preguntas (quien atiende puede no tener el documento a mano).
 */

type Errores = Record<string, string>

/** Un control por tipo de pregunta, para los tipos que se piden al reservar. */
export const ControlDePregunta = ({ campo, valor, cambiar, error }: { campo: CampoDeReserva; valor: unknown; cambiar: (v: unknown) => void; error?: string }) => {
  const id = `pregunta-${campo.clave}`
  const etiqueta = (
    <label className="text-[13px] font-medium text-tinta" htmlFor={id}>
      {campo.etiqueta}
      {campo.obligatorio ? <span className="text-tenue"> (en la web es obligatorio)</span> : null}
    </label>
  )
  const pie = error ? (
    <span className="text-xs text-error" role="alert">
      {error}
    </span>
  ) : campo.ayuda ? (
    <span className="text-xs text-tenue">{campo.ayuda}</span>
  ) : null
  if (campo.tipo === 'booleano') {
    return (
      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-2 text-sm">
          <input checked={valor === true} id={id} onChange={(e) => cambiar(e.target.checked)} type="checkbox" />
          {campo.etiqueta}
        </label>
        {pie}
      </div>
    )
  }
  if ((campo.tipo === 'radio' || campo.tipo === 'selector') && campo.opciones) {
    return (
      <div className="flex flex-col gap-1.5">
        {etiqueta}
        <Selector id={id} onChange={(e) => cambiar(e.target.value || null)} value={typeof valor === 'string' ? valor : ''}>
          <option value="">—</option>
          {campo.opciones.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
        </Selector>
        {pie}
      </div>
    )
  }
  if (campo.tipo === 'area') {
    return (
      <div className="flex flex-col gap-1.5">
        {etiqueta}
        <textarea className={clases(claseDeEntrada(error), 'min-h-20 py-2')} id={id} onChange={(e) => cambiar(e.target.value)} value={typeof valor === 'string' ? valor : ''} />
        {pie}
      </div>
    )
  }
  const tipo = campo.tipo === 'numero' ? 'number' : campo.tipo === 'fecha' ? 'date' : campo.tipo === 'hora' ? 'time' : campo.tipo === 'email' ? 'email' : campo.tipo === 'telefono' ? 'tel' : campo.tipo === 'url' ? 'url' : 'text'
  return (
    <div className="flex flex-col gap-1.5">
      {etiqueta}
      <input
        className={claseDeEntrada(error)}
        id={id}
        onChange={(e) => cambiar(tipo === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
        type={tipo}
        value={valor === null || valor === undefined ? '' : String(valor)}
      />
      {pie}
    </div>
  )
}

/** Los tipos que el formulario sabe pedir; el resto (relaciones, ficheros…) se rellenan desde la ficha. */
const SE_PIDE = new Set(['texto', 'area', 'numero', 'email', 'url', 'telefono', 'fecha', 'hora', 'booleano', 'radio', 'selector'])

export const NuevaReserva = ({ sitio, datos, fecha, cerrar, hecha }: { sitio: number; datos: Calendario; fecha: string; cerrar: () => void; hecha: () => void }) => {
  const [servicioId, setServicioId] = useState<number>(datos.servicios[0]?.id ?? 0)
  const servicio = datos.servicios.find((s) => s.id === servicioId)
  const [dia, setDia] = useState(fecha)
  const [recurso, setRecurso] = useState<number | ''>('')
  const [cantidad, setCantidad] = useState(1)
  const [desde, setDesde] = useState<null | string>(null)
  const [cuantas, setCuantas] = useState(1)
  const [contacto, setContacto] = useState({ nombre: '', telefono: '', email: '' })
  const [origen, setOrigen] = useState<'panel' | 'telefono' | 'whatsapp'>('telefono')
  const [respuestas, setRespuestas] = useState<Record<string, unknown>>({})
  const [notas, setNotas] = useState('')
  const [errores, setErrores] = useState<Errores>({})
  const [fallo, setFallo] = useState<null | string>(null)
  const [enviando, setEnviando] = useState(false)
  const esEstancia = servicio?.tipo === 'estancia'
  const nombreDe = (id: number) => datos.recursos.find((r) => r.id === id)?.nombre ?? `Recurso ${id}`

  const huecos = useQuery({
    queryKey: ['agenda-huecos', sitio, servicioId, dia, recurso, cantidad],
    queryFn: () => pedir<{ huecos: Hueco[] }>(`/agenda/huecos${consulta({ sitio, servicio: servicioId, fecha: dia, recurso: recurso || undefined, cantidad })}`),
    enabled: Boolean(servicio) && !esEstancia,
  })

  const enviar = async () => {
    setFallo(null)
    setErrores({})
    if (!contacto.nombre.trim()) return setErrores({ nombre: 'Escribe el nombre de quien reserva.' })
    if (!esEstancia && !desde) return setFallo('Elige una hora libre.')
    setEnviando(true)
    try {
      await pedir('/agenda/reservas', {
        metodo: 'POST',
        cuerpo: { sitio, servicio: servicioId, ...(esEstancia ? { entrada: dia, cuantas } : { desde }), recurso: recurso || undefined, cantidad, ...contacto, origen, notas: notas || undefined, datos: respuestas },
      })
      hecha()
    } catch (e) {
      if (e instanceof ErrorDeLaApi) {
        const porCampo = (e.datos.errores ?? {}) as Errores
        setErrores(porCampo)
        setFallo(e.message)
      } else setFallo('No se ha podido guardar la reserva. Vuelve a intentarlo.')
    } finally {
      setEnviando(false)
    }
  }

  if (!servicio) {
    return (
      <Modal ancho="max-w-xl" cerrar={cerrar} titulo="Nueva reserva">
        <Aviso tono="info">Todavía no hay servicios que se reserven. Créalos en Servicios, en su pestaña «Reserva en la agenda».</Aviso>
      </Modal>
    )
  }

  return (
    <Modal
      ancho="max-w-2xl"
      cerrar={cerrar}
      pie={
        <>
          <Boton onClick={cerrar} tipo="discreto">
            Cancelar
          </Boton>
          <Boton cargando={enviando} onClick={enviar} tipo="primario">
            Guardar la reserva
          </Boton>
        </>
      }
      titulo="Nueva reserva"
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Servicio</span>
            <Selector
              data-autofocus
              onChange={(e) => {
                setServicioId(Number(e.target.value))
                setDesde(null)
                setRecurso('')
                setCantidad(1)
                setRespuestas({})
              }}
              value={servicioId}
            >
              {datos.servicios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                  {s.publicado ? '' : ' (en borrador)'}
                </option>
              ))}
            </Selector>
          </label>
          {servicio.recursos.length > 1 && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">Con</span>
              <Selector
                onChange={(e) => {
                  setRecurso(e.target.value ? Number(e.target.value) : '')
                  setDesde(null)
                }}
                value={recurso}
              >
                <option value="">Quien esté libre</option>
                {servicio.recursos.map((r) => (
                  <option key={r} value={r}>
                    {nombreDe(r)}
                  </option>
                ))}
              </Selector>
            </label>
          )}
          <Campo
            etiqueta={esEstancia ? 'Fecha de entrada' : 'Día'}
            onChange={(e) => {
              setDia(e.target.value)
              setDesde(null)
            }}
            type="date"
            value={dia}
          />
          {esEstancia ? (
            <Campo etiqueta="Noches o días" min={1} onChange={(e) => setCuantas(Math.max(1, Number(e.target.value) || 1))} type="number" value={cuantas} />
          ) : servicio.cantidadMaxima > 1 ? (
            <Campo
              etiqueta={servicio.tipo === 'mesa' ? 'Comensales' : 'Plazas'}
              max={servicio.cantidadMaxima}
              min={1}
              onChange={(e) => {
                setCantidad(Math.min(servicio.cantidadMaxima, Math.max(1, Number(e.target.value) || 1)))
                setDesde(null)
              }}
              type="number"
              value={cantidad}
            />
          ) : null}
        </div>

        {!esEstancia && (
          <div>
            <p className="mb-2 text-[13px] font-medium">Hora libre el {textoDeFecha(dia)}</p>
            {huecos.isPending ? (
              <p className="text-sm text-tenue">Buscando huecos…</p>
            ) : huecos.error ? (
              <Aviso tono="error">{huecos.error instanceof ErrorDeLaApi ? huecos.error.message : 'No se han podido cargar los huecos.'}</Aviso>
            ) : (huecos.data?.huecos ?? []).length === 0 ? (
              <p className="text-sm text-tenue">No queda ningún hueco ese día. Prueba otro día{servicio.recursos.length > 1 ? ' u otra persona' : ''}.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" role="group">
                {huecos.data!.huecos.map((h) => (
                  <button
                    aria-pressed={desde === h.desde}
                    className={clases('h-8 rounded-boton border px-2.5 text-[13px]', desde === h.desde ? 'border-violeta bg-violeta text-white' : 'border-linea bg-papel hover:border-violeta')}
                    key={h.desde}
                    onClick={() => setDesde(h.desde)}
                    type="button"
                  >
                    {h.hora}
                    {h.quedan !== undefined && servicio.tipo === 'cupo' ? ` · ${h.quedan}` : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Campo error={errores.nombre} etiqueta="Nombre" onChange={(e) => setContacto((c) => ({ ...c, nombre: e.target.value }))} value={contacto.nombre} />
          <Campo etiqueta="Teléfono" onChange={(e) => setContacto((c) => ({ ...c, telefono: e.target.value }))} type="tel" value={contacto.telefono} />
          <Campo error={errores.email} etiqueta="Correo" onChange={(e) => setContacto((c) => ({ ...c, email: e.target.value }))} type="email" value={contacto.email} />
        </div>
        <label className="flex max-w-xs flex-col gap-1.5">
          <span className="text-[13px] font-medium">Cómo llegó</span>
          <Selector onChange={(e) => setOrigen(e.target.value as typeof origen)} value={origen}>
            <option value="telefono">Por teléfono</option>
            <option value="whatsapp">Por WhatsApp</option>
            <option value="panel">En persona</option>
          </Selector>
        </label>

        {servicio.campos.filter((c) => SE_PIDE.has(c.tipo)).length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {servicio.campos
              .filter((c) => SE_PIDE.has(c.tipo))
              .map((c) => (
                <ControlDePregunta cambiar={(v) => setRespuestas((r) => ({ ...r, [c.clave]: v }))} campo={c} error={errores[`datos.${c.clave}`]} key={c.clave} valor={respuestas[c.clave]} />
              ))}
          </div>
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Notas internas</span>
          <textarea className={clases(claseDeEntrada(), 'min-h-16 py-2')} onChange={(e) => setNotas(e.target.value)} value={notas} />
        </label>
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </div>
    </Modal>
  )
}
